/**
 * "Create new Company" — a three-step dialog (ADR-022):
 *
 *   1. Name.
 *   2. Shared workspaces YOU OWN to link now. Because the creator is the
 *      owner of each one, the wizard can propose AND accept in one go — the
 *      same two-sided act, with the same give-ups disclosed here that the
 *      accept screen shows. Workspaces owned by others are proposed later
 *      from the Company page and accepted by their owner.
 *   3. People to invite to the Company roster (owner / admin — management,
 *      never data). Each is an invitation: they hold the seat only once they
 *      accept it from their own account.
 *   4. Arches to approve at Company level — each through the same
 *      server-built disclosure the approvals page shows — and the "Trust
 *      Fias arches" default.
 *
 * Only the name is required; every later step is optional and can be done
 * from the Company page afterwards.
 *
 * Everything runs on the final step: create, then links, then invitations. A
 * failure on one item is reported on that item and does not undo the
 * Company — the page shows the same state afterwards and each item can be
 * retried from there.
 */

import { useEffect, useMemo, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserSearchCombobox } from '@/components/UserSearchCombobox';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import { api, type ArcheApprovalDisclosureDto, type CompanyMemberRole } from '@/lib/api';
import { disclosedAccessPolicy } from '../lib/approval-access-policy';
import { queryKeys } from '@/lib/query-keys';
import { ArcheSearchCombobox, publisherLabel } from './ArcheSearchCombobox';
import { companyErrorCode, describeCompanyError } from '../lib/company-errors';

interface CreateCompanyWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new Company's id once it exists (even if later steps failed). */
  onCreated: (companyId: string) => void;
}

type Step = 'name' | 'workspaces' | 'people' | 'arches' | 'done';
const STEPS: Step[] = ['name', 'workspaces', 'people', 'arches'];

interface PersonRow {
  key: number;
  search: string;
  username: string | null;
  role: CompanyMemberRole;
}

interface Outcome {
  label: string;
  ok: boolean;
  detail?: string;
}

const describe = (err: unknown): string => describeCompanyError(err, 'Failed.');

export function CreateCompanyWizard({ open, onOpenChange, onCreated }: CreateCompanyWizardProps) {
  const qc = useQueryClient();
  const switcher = useHolderSwitcher({ defaultToPersonal: false });
  const ownedWorkspaces = useMemo(
    () => switcher.options.filter((o) => o.holderType === 'org' && o.role === 'owner'),
    [switcher.options],
  );

  const [step, setStep] = useState<Step>('name');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [trustFias, setTrustFias] = useState(false);
  const [archeQuery, setArcheQuery] = useState('');
  const [candidate, setCandidate] = useState<ArcheApprovalDisclosureDto | null>(null);
  const [candidateError, setCandidateError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<ArcheApprovalDisclosureDto[]>([]);

  useEffect(() => {
    if (!open) {
      setStep('name');
      setName('');
      setPicked(new Set());
      setPeople([]);
      setBusy(false);
      setError(null);
      setOutcomes([]);
      setTrustFias(false);
      setArcheQuery('');
      setCandidate(null);
      setCandidateError(null);
      setChosen([]);
    }
  }, [open]);

  // A slower earlier preview must not overwrite a newer pick.
  const previewRequest = useRef(0);
  async function previewArche(archeId: string): Promise<void> {
    const token = ++previewRequest.current;
    setCandidateError(null);
    setCandidate(null);
    try {
      const { disclosure } = await api.getArcheCatalogDisclosure(archeId);
      if (token === previewRequest.current) setCandidate(disclosure);
    } catch (err) {
      if (token === previewRequest.current) setCandidateError(describe(err));
    }
  }

  function addCandidate(): void {
    if (!candidate) return;
    setChosen((list) =>
      list.some((c) => c.archeId === candidate.archeId) ? list : [...list, candidate],
    );
    setCandidate(null);
    setArcheQuery('');
  }

  const stepIndex = STEPS.indexOf(step);
  const trimmedName = name.trim();
  const validPeople = people.filter((p) => p.username);

  function togglePicked(holderId: string, on: boolean): void {
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(holderId);
      else next.delete(holderId);
      return next;
    });
  }

  function addPersonRow(): void {
    setPeople((rows) => [...rows, { key: Date.now(), search: '', username: null, role: 'admin' }]);
  }

  async function run(): Promise<void> {
    setBusy(true);
    setError(null);
    const results: Outcome[] = [];
    let companyId: string;
    try {
      const { company } = await api.createCompany(trimmedName);
      companyId = company.companyId;
      results.push({ label: `Created ${company.name}`, ok: true });
    } catch (err) {
      setError(describe(err));
      setBusy(false);
      return;
    }

    for (const holderId of picked) {
      const ws = ownedWorkspaces.find((o) => o.holderId === holderId);
      const label = `Linked ${ws?.displayName ?? holderId}`;
      let invitationId: string | null = null;
      try {
        const { invitation } = await api.proposeCompanyLink(companyId, holderId);
        invitationId = invitation.invitationId;
        // The creator owns this workspace, so accepting is their own act. No
        // owners-as-admins here: the creator is the only Company owner and is
        // already on this roster.
        await api.acceptCompanyLink(invitation.invitationId, { addOwnersAsAdmins: false });
        results.push({ label, ok: true });
        void qc.invalidateQueries({ queryKey: queryKeys.vault.company(holderId) });
      } catch (err) {
        // Half done (proposed, not accepted): withdraw so the workspace is
        // linkable again from the Company page rather than stuck "invited".
        if (invitationId) {
          try {
            await api.cancelCompanyLink(companyId, invitationId);
          } catch {
            // best effort — the original failure is what the user sees
          }
        }
        results.push({ label, ok: false, detail: describe(err) });
      }
    }

    for (const p of validPeople) {
      const label = `Invited @${p.username} as ${p.role}`;
      try {
        await api.addCompanyMember(companyId, { username: p.username!, role: p.role });
        results.push({ label, ok: true, detail: 'They join when they accept.' });
      } catch (err) {
        results.push({ label, ok: false, detail: describe(err) });
      }
    }

    if (trustFias) {
      try {
        await api.updateCompany(companyId, { trustFiasArches: true });
        results.push({ label: 'Trust Fias arches: on', ok: true });
      } catch (err) {
        results.push({ label: 'Trust Fias arches: on', ok: false, detail: describe(err) });
      }
    }

    for (const arche of chosen) {
      const label = `Approved ${arche.name}`;
      try {
        // Approves exactly the roles the disclosure showed when it was chosen.
        await api.decideCompanyApproval(companyId, {
          archeId: arche.archeId,
          decision: 'approve',
          ...disclosedAccessPolicy(arche),
        });
        results.push({ label, ok: true });
      } catch (err) {
        results.push({
          label,
          ok: false,
          detail:
            companyErrorCode(err) === 'ACCESS_POLICY_CHANGED'
              ? 'Its roles changed after you chose it, so it was not approved. Approve it from the Company’s Approvals page to review them as they are now.'
              : describe(err),
        });
      }
    }

    // The page selects the new Company by its id in the URL, and it can only
    // select what the list knows: wait for the refetch before handing off.
    await qc.invalidateQueries({ queryKey: queryKeys.companies.mine() });
    void qc.invalidateQueries({ queryKey: queryKeys.companies.detail(companyId) });
    void qc.invalidateQueries({ queryKey: queryKeys.companies.approvals(companyId) });
    // Approvals and Trust Fias made here show on every linked workspace's
    // card; the owners' invitation feed may have changed too.
    void qc.invalidateQueries({ queryKey: queryKeys.vault.approvalsAll });
    void qc.invalidateQueries({ queryKey: queryKeys.vault.me.companyLinkProposals() });
    setOutcomes(results);
    setStep('done');
    setBusy(false);
    onCreated(companyId);
  }

  const canNext = step === 'name' ? trimmedName.length > 0 && trimmedName.length <= 120 : !busy;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" aria-hidden="true" />
            {step === 'done' ? 'Company created' : 'Create new Company'}
          </DialogTitle>
          {step !== 'done' && (
            <DialogDescription>
              Step {stepIndex + 1} of {STEPS.length} — {step === 'name' && 'name it'}
              {step === 'workspaces' && 'link the shared workspaces you own (optional)'}
              {step === 'people' && 'invite the people who will manage it (optional)'}
              {step === 'arches' && 'approve arches for every linked workspace (optional)'}
            </DialogDescription>
          )}
        </DialogHeader>

        <DialogBody>
          {step === 'name' && (
            <div className="space-y-3">
              <div className="grid gap-1.5">
                <Label htmlFor="wizard-company-name">Company name</Label>
                <Input
                  id="wizard-company-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={120}
                  placeholder="Acme Ltd"
                  autoFocus
                />
              </div>
              <p className="text-sm text-muted-foreground">
                You become its first owner and its payer. A Company holds no data of its own; it
                unifies billing, administration and arche approvals across the workspaces it links.
              </p>
            </div>
          )}

          {step === 'workspaces' && (
            <div className="space-y-3">
              {ownedWorkspaces.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  You don’t own any shared workspaces yet. You can invite workspaces from the
                  Company page later; each workspace’s owner accepts.
                </p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Optional — you can link workspaces later from the Company page. These are shared
                    workspaces you own, so they link as soon as the Company exists; others can be
                    invited later; their owner accepts.
                  </p>
                  <ul className="divide-y divide-border rounded-md border border-border">
                    {ownedWorkspaces.map((o) => (
                      <li key={o.holderId} className="flex items-center gap-3 px-3 py-2">
                        <Checkbox
                          id={`wizard-ws-${o.holderId}`}
                          checked={picked.has(o.holderId)}
                          onCheckedChange={(v) => togglePicked(o.holderId, v === true)}
                        />
                        <Label htmlFor={`wizard-ws-${o.holderId}`} className="text-sm">
                          {o.displayName}
                        </Label>
                      </li>
                    ))}
                  </ul>
                  {picked.size > 0 && (
                    <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      Linking a workspace: its billing moves to the Company’s payer (you)
                      immediately, only the Company can unlink it afterwards, and arches the Company
                      approves can read its data on behalf of members at their own role. Roster,
                      roles and data stay exactly as they are.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {step === 'people' && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Optional — you can invite people later from the Company page. Each person is sent an
                invitation and joins the Company when they accept it. Company roles are management
                only: they grant no access to any workspace’s data. Owners and admins manage the
                roster and approvals and propose workspace links; owners alone unlink workspaces,
                hold the payer role and add or remove owners.
              </p>
              {people.map((row, i) => (
                <div key={row.key} className="flex flex-wrap items-end gap-2">
                  <div className="grid min-w-[12rem] flex-1 gap-1.5">
                    <Label htmlFor={`wizard-person-${row.key}`}>Person</Label>
                    <UserSearchCombobox
                      id={`wizard-person-${row.key}`}
                      value={row.search}
                      onValueChange={(v) =>
                        setPeople((rows) =>
                          rows.map((r, j) => (j === i ? { ...r, search: v, username: null } : r)),
                        )
                      }
                      onSelectUser={(u) =>
                        setPeople((rows) =>
                          rows.map((r, j) => (j === i ? { ...r, username: u.username } : r)),
                        )
                      }
                      placeholder="@username"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label htmlFor={`wizard-role-${row.key}`}>Role</Label>
                    <select
                      id={`wizard-role-${row.key}`}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                      value={row.role}
                      onChange={(e) =>
                        setPeople((rows) =>
                          rows.map((r, j) =>
                            j === i ? { ...r, role: e.target.value as CompanyMemberRole } : r,
                          ),
                        )
                      }
                    >
                      <option value="admin">Admin</option>
                      <option value="owner">Owner</option>
                    </select>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label="Remove person"
                    onClick={() => setPeople((rows) => rows.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={addPersonRow}>
                Add a person
              </Button>
            </div>
          )}

          {step === 'arches' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Optional — you can approve arches later from the Company page. A Company-level
                approval lets an arche work with every linked workspace’s data on behalf of members,
                at each member’s own role; any workspace can still deny it, and revoking is
                immediate. Built-in Fias features (Todo Lists, Calendar, …) follow workspace roles
                and need no approval.
              </p>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="wizard-trust-fias"
                  checked={trustFias}
                  onCheckedChange={(v) => setTrustFias(v === true)}
                />
                <Label htmlFor="wizard-trust-fias" className="text-sm leading-tight">
                  Trust Fias arches — approve the platform’s own first-party arches, from a fixed
                  reviewed list, on every linked workspace; their files and team data there are
                  billed to the workspace. Switchable later.
                </Label>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="wizard-arche-search">Approve an arche</Label>
                <ArcheSearchCombobox
                  id="wizard-arche-search"
                  scopeKey="catalog"
                  search={(q) => api.searchArcheCatalog(q)}
                  value={archeQuery}
                  onValueChange={(v) => {
                    setArcheQuery(v);
                    setCandidate(null);
                  }}
                  onSelect={(r) => void previewArche(r.archeId)}
                />
              </div>
              {candidateError && <p className="text-sm text-destructive">{candidateError}</p>}

              {candidate && (
                <div className="space-y-2 rounded-md border border-border p-3 text-sm">
                  <p className="font-medium text-foreground">{candidate.name}</p>
                  <p className="text-xs text-muted-foreground">{publisherLabel(candidate)}</p>
                  {candidate.reach.length === 0 ? (
                    <p className="text-muted-foreground">
                      Declares no permission that an approval widens.
                    </p>
                  ) : (
                    <ul className="list-disc space-y-1 pl-5">
                      {candidate.reach.map((r) => (
                        <li key={r.entityId}>{r.sentence}</li>
                      ))}
                    </ul>
                  )}
                  {candidate.permissions.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Declares: {candidate.permissions.join(', ')}
                    </p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setCandidate(null)}>
                      Not this one
                    </Button>
                    <Button size="sm" onClick={addCandidate}>
                      Add to approvals
                    </Button>
                  </div>
                </div>
              )}

              {chosen.length > 0 && (
                <ul className="divide-y divide-border rounded-md border border-border">
                  {chosen.map((c) => (
                    <li
                      key={c.archeId}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-sm">
                        <span className="font-medium text-foreground">{c.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">
                          {publisherLabel(c)}
                        </span>
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`Remove ${c.name}`}
                        onClick={() =>
                          setChosen((list) => list.filter((x) => x.archeId !== c.archeId))
                        }
                      >
                        <X className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 'done' && (
            <ul className="space-y-1 text-sm">
              {outcomes.map((o, i) => (
                <li key={`${i}-${o.label}`} className="flex items-start gap-2">
                  {o.ok ? (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  ) : (
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />
                  )}
                  <span>
                    {o.label}
                    {o.detail ? (
                      <span className="block text-xs text-muted-foreground">{o.detail}</span>
                    ) : null}
                  </span>
                </li>
              ))}
              {outcomes.some((o) => !o.ok) && (
                <li className="pt-1 text-xs text-muted-foreground">
                  Anything that failed can be retried from the Company page.
                </li>
              )}
            </ul>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </DialogBody>

        <DialogFooter className="gap-2">
          {step === 'done' ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              {stepIndex > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setStep(STEPS[stepIndex - 1])}
                  disabled={busy}
                >
                  Back
                </Button>
              )}
              {stepIndex < STEPS.length - 1 ? (
                <Button onClick={() => setStep(STEPS[stepIndex + 1])} disabled={!canNext}>
                  Next
                </Button>
              ) : (
                <Button onClick={run} disabled={busy || !trimmedName}>
                  {busy ? 'Creating…' : 'Create Company'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
